'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { DESIGN_TYPES, TITLE_MAX, TITLE_MIN, normaliseTitle, titleProblem, type DesignType } from '@/app/lib/community/shared';

type Result =
  | { kind: 'published'; status: string; path: string }
  | { kind: 'already'; status?: string; path?: string }
  | { kind: 'error'; message: string }
  | null;

/** The saved build a publish comes from. `created`: this publish attempt saved it on the user's behalf. */
export type PublishSource = { id: number; created?: boolean };

export interface PublishPanelProps {
  designType: DesignType;
  defaultTitle: string;
  /**
   * The saved build this publish comes from, resolved when the user presses Publish — so an editor can
   * save (or find the identical build already saved) at that moment, while My Library passes its own id.
   */
  resolveElementId: (title: string) => Promise<PublishSource | { error: string }>;
  /** The game version the user is working in, shown on the design as "Made for". Omit to use the saved build's. */
  version?: string;
  /** The type's own state check, run before anything is saved (advisory; the server re-checks). */
  precheck?: () => string | null;
  /**
   * The server REFUSED the publish (a 4xx: title, username, cooldown, already published, not found). Awaited
   * before the form settles, so a caller that saved a build for this attempt can take it back first.
   */
  onRefused?: (source: PublishSource, status: number) => void | Promise<void>;
  onPublished?: () => void;
  /** Open straight away, e.g. when a "Publish to community" button outside the panel was pressed. */
  startOpen?: boolean;
  /** Called on Cancel and Done. When given, the parent removes the panel (and must place focus). */
  onClose?: () => void;
  disabled?: boolean;
}

/**
 * "Publish to community" — the one publish form, used from My Library and from an editor.
 *
 * Either way the request names a SAVED BUILD by id, never the design itself: the server reads the
 * state from the caller's own row, so only what is really in their library can be published.
 */
export default function PublishPanel({
  designType, defaultTitle, resolveElementId, version, precheck, onRefused, onPublished, startOpen = false, onClose, disabled = false,
}: PublishPanelProps) {
  const [open, setOpen] = useState(startOpen);
  const [title, setTitle] = useState(defaultTitle.slice(0, TITLE_MAX));
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<Result>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const publishedRef = useRef<HTMLDivElement>(null);

  const problem = titleProblem(normaliseTitle(title));
  const empty = title.trim() === '';

  // The form (and focus with it) is replaced by the result; move focus to the result so it is not lost.
  useEffect(() => {
    if (result?.kind === 'published') publishedRef.current?.focus();
  }, [result]);

  const publish = async () => {
    if (problem) return;
    setResult(null);
    const refused = precheck?.();
    if (refused) {
      setResult({ kind: 'error', message: refused });
      return;
    }
    setSubmitting(true);
    try {
      const source = await resolveElementId(normaliseTitle(title));
      if ('error' in source) {
        setResult({ kind: 'error', message: source.error });
        return;
      }
      const res = await fetch('/api/designs/publish', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ elementId: source.id, title: normaliseTitle(title), ...(version ? { version } : {}) }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && typeof data.path === 'string') {
        setResult({ kind: 'published', status: String(data.status), path: data.path });
        onPublished?.();
        return;
      }
      // A definite refusal (4xx). A 5xx or network error may still have published, so nothing is undone.
      if (res.status >= 400 && res.status < 500) await onRefused?.(source, res.status);
      if (res.status === 409) {
        setResult({ kind: 'already', status: typeof data.status === 'string' ? data.status : undefined, path: typeof data.path === 'string' ? data.path : undefined });
      } else {
        setResult({ kind: 'error', message: typeof data.error === 'string' ? data.error : 'Could not publish. Try again.' });
      }
    } catch {
      setResult({ kind: 'error', message: 'Network error. Try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setOpen(false);
    setResult(null);
    if (onClose) onClose();
    else requestAnimationFrame(() => triggerRef.current?.focus());
  };

  if (!open) {
    return (
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-[12.5px] border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:text-emerald-200 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M12 16V4m0 0l-4 4m4-4l4 4M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2" /></svg>
        Publish to community
      </button>
    );
  }

  if (result?.kind === 'published') {
    return (
      <div ref={publishedRef} tabIndex={-1} role="status" className="w-full rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2.5 text-[12.5px] text-emerald-200 space-y-2 focus:outline-none">
        <p>
          {result.status === 'approved' ? 'Published — it is live in the gallery.' : 'Sent for review. It will appear in the gallery once a moderator approves it.'}{' '}
          <Link href={result.path} className="underline hover:text-white">View design</Link>
          {' · '}
          <Link href="/saved?tab=published" className="underline hover:text-white">Your published designs</Link>
        </p>
        <button type="button" onClick={close} className="rounded-md px-2.5 py-1 text-[12px] border border-emerald-500/30 text-emerald-200 hover:text-white">
          Done
        </button>
      </div>
    );
  }

  const hintId = `publish-title-hint-${designType}`;
  return (
    <div className="w-full rounded-lg border border-zinc-800 bg-zinc-900/60 p-3 space-y-2.5">
      <label htmlFor={`publish-title-${designType}`} className="block text-[12px] font-semibold text-zinc-300">Title shown in the gallery</label>
      <input
        id={`publish-title-${designType}`}
        value={title}
        maxLength={TITLE_MAX}
        placeholder="e.g. Royal Guard"
        autoFocus
        onChange={e => setTitle(e.target.value)}
        aria-describedby={hintId}
        className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-[13px] text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-emerald-500"
      />
      {/* Always say why Publish is unavailable — an empty title used to leave it greyed out in silence. */}
      <p id={hintId} className={`text-[11.5px] ${empty ? 'text-zinc-500' : 'text-amber-300'}`}>
        {empty ? `Give it a title — ${TITLE_MIN} to ${TITLE_MAX} characters.` : problem}
      </p>
      <p className="text-[11.5px] text-zinc-500 leading-relaxed">
        Anyone can view, like and copy designs you publish. Your username is shown; your email never is.{' '}
        {DESIGN_TYPES[designType].publishNote} Every design is checked by a moderator before it appears.
        See the <Link href="/terms" className="underline hover:text-zinc-300">terms</Link>.
      </p>
      {result?.kind === 'already' && (
        <p role="status" className="text-[12px] text-zinc-300">
          {result.status === 'removed'
            ? 'A moderator removed this design, so it can’t be published again.'
            : 'You have already published this design.'}{' '}
          {result.path && <Link href={result.path} className="underline hover:text-white">View it</Link>}
        </p>
      )}
      {result?.kind === 'error' && <p role="alert" className="text-[12px] text-red-400">{result.message}</p>}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={publish}
          disabled={submitting || !!problem}
          aria-describedby={problem ? hintId : undefined}
          className="rounded-lg px-3 py-1.5 text-[12.5px] font-semibold bg-emerald-600 hover:bg-emerald-500 text-emerald-950 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Publishing…' : 'Publish'}
        </button>
        <button type="button" onClick={close} className="rounded-lg px-3 py-1.5 text-[12.5px] text-zinc-400 hover:text-white">
          Cancel
        </button>
      </div>
    </div>
  );
}
